/**
 * Member detail - FR-8.4's aggregate.
 *
 * Four things about one member, from four different specs' endpoints, in one
 * screen:
 *
 *   profile           spec 03  GET /admin/members/{id}
 *   membership        spec 06  GET /admin/members/{id}/memberships
 *   diet activity     spec 08  GET /admin/ai-requests?user_id=…
 *   FamilyBiz         spec 10  GET /admin/family-biz?owner_q=<mobile>
 *   UNO hand-off      spec 16  (on the profile payload)
 *   Health policy     spec 22  GET /admin/members/{id}/health-policy
 *
 * Each is fetched separately rather than as one composite payload, so a slow or
 * failing section degrades to its own error notice instead of blanking the page.
 *
 * TWO THINGS THIS SCREEN DOES NOT SHOW, both deliberate:
 *
 * 1. **Contact activity (FR-8.4's fifth item).** Spec 11's admin endpoints
 *    aggregate BY TARGET - vendor, listing, date range - and offer no member
 *    filter, and there is no member-scoped route. Contact taps are member
 *    self-service and are deliberately not written to `audit_logs` (see
 *    `services/audit_service.py`), so they cannot be recovered from there either.
 *    The section below states this plainly rather than showing an empty table that
 *    would read as "this member has never contacted anyone". Filed as BACKLOG B-52.
 * 2. **Body metrics and diet inputs.** Height, weight (current, target and
 *    history), dietary preferences and health conditions are member-only by
 *    policy. The API does not return them to an admin, so there is nothing to
 *    show - not even a read-only view.
 *
 * Spec 19: an admin can delete a member's account from here (Delete account),
 * and a deleted member - by either path - is read-only: the API refuses every
 * edit and status change with 409 `account_deleted`, so the screen shows a
 * banner instead of the controls.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Definition,
  DefinitionList,
  ErrorNotice,
  EmptyState,
  Field,
  Input,
  LoadingRows,
  Modal,
  Page,
  Select,
  SuccessNotice,
  Tabs,
  Textarea,
  statusTone,
} from "../components/primitives";
import {
  deleteMember,
  fetchMember,
  updateMember,
  type MemberDetail,
  type MemberUpdate,
  type StatusEvent,
} from "../lib/api/accounts";
import { fetchAiRequests, type AiRequestEntry } from "../lib/api/ai";
import {
  fetchHealthPolicy,
  removeHealthPolicy,
  updateHealthPolicy,
  uploadHealthPolicyFile,
  type HealthPolicy,
} from "../lib/api/health-policy";
import { fetchListings, type ListingAdmin } from "../lib/api/familybiz";
import { fetchMemberships, type MembershipList } from "../lib/api/memberships";
import type { MemberStatus } from "../lib/api/types";
import { confirmAction, useMutation, useQuery } from "../lib/hooks";
import { formatCount, formatDate, formatDateTime, humanizeStatus, orDash } from "../lib/format";

type Tab = "overview" | "diet" | "listings" | "history";

// Not `deleted`: deletion scrubs data, so it is its own action (Delete
// account), and the API refuses it as a status change.
const STATUSES: MemberStatus[] = ["pending", "active", "suspended", "rejected"];

export function MemberDetailScreen() {
  const { memberId = "" } = useParams();
  const [tab, setTab] = useState<Tab>("overview");
  const [editing, setEditing] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // A screen that navigates here after creating the member (spec 17) hands
  // its confirmation over in router state; read once, then owned locally.
  const location = useLocation();
  const [saved, setSaved] = useState<string | null>(
    () => (location.state as { saved?: string } | null)?.saved ?? null,
  );

  const member = useQuery<MemberDetail>(() => fetchMember(memberId), [memberId]);

  if (member.error) {
    return (
      <Page>
        <ErrorNotice error={member.error} onRetry={member.reload} />
        <Link to="/members" className="btn btn--ghost" style={{ alignSelf: "flex-start" }}>
          Back to members
        </Link>
      </Page>
    );
  }

  if (member.loading && !member.data) {
    return (
      <Page>
        <Card flush>
          <LoadingRows rows={4} columns={3} />
        </Card>
      </Page>
    );
  }

  if (!member.data) return null;

  const detail = member.data;
  const isDeleted = detail.status === "deleted";
  // The event the API writes on deletion: a null actor means the member did it
  // themselves; otherwise an admin did, with an optional reason in `remarks`.
  const deletion = isDeleted
    ? detail.status_events
        .filter((event) => event.to_status === "deleted")
        .reduce<StatusEvent | null>(
          (latest, event) => (latest && latest.created_at > event.created_at ? latest : event),
          null,
        )
    : null;
  const deletedOn = deletion ? ` on ${formatDateTime(deletion.created_at)}` : "";

  return (
    <Page>
      {saved ? <SuccessNotice message={saved} onDismiss={() => setSaved(null)} /> : null}

      {isDeleted ? (
        <p className="notice" role="status" style={{ margin: 0 }}>
          <strong>
            {deletion?.actor_admin_id
              ? `An admin deleted this account${deletedOn}.`
              : `This member deleted their account${deletedOn}.`}
          </strong>{" "}
          {deletion?.remarks ? <>Reason: {deletion.remarks}. </> : null}
          Their profile, photo, weight, diet plans and FamilyBiz listings were removed. Membership
          records and status history are kept. The account can't be edited or restored.
        </p>
      ) : null}

      <Card>
        <CardHeader
          title={
            <span className="row row--tight">
              {detail.full_name}
              <Badge tone={statusTone(detail.status)}>{detail.status}</Badge>
              {detail.is_demo_account ? <Badge tone="plain">demo account</Badge> : null}
              {detail.uno_approved_at ? <Badge tone="active">UNO approved</Badge> : null}
            </span>
          }
          hint={
            // A deleted member's mobile is the placeholder `deleted:<id>`.
            isDeleted
              ? "Personal details removed"
              : `${detail.mobile_number}${detail.email ? ` · ${detail.email}` : ""}`
          }
          actions={
            <>
              {isDeleted ? null : (
                <>
                  <Button size="sm" onClick={() => setEditing(true)}>
                    Edit profile
                  </Button>
                  <Button size="sm" variant="accent" onClick={() => setChangingStatus(true)}>
                    Change status
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={detail.is_demo_account}
                    title={detail.is_demo_account ? "The demo account can't be deleted." : undefined}
                    onClick={() => setDeleting(true)}
                  >
                    Delete account
                  </Button>
                </>
              )}
              <Link to="/members" className="btn btn--ghost btn--sm">
                Back
              </Link>
            </>
          }
        />

        <div className="row" style={{ gap: 24, alignItems: "flex-start" }}>
          <Avatar name={detail.full_name} url={detail.profile_image_url} />
          <div className="grow">
            <DefinitionList>
              <Definition term="Member id">
                <span className="mono">{detail.id}</span>
              </Definition>
              <Definition term="Registered">{formatDateTime(detail.created_at)}</Definition>
              <Definition term="Approved">
                {detail.approved_at ? formatDateTime(detail.approved_at) : "Not approved"}
              </Definition>
              <Definition term="Last sign-in">
                {detail.last_login_at ? formatDateTime(detail.last_login_at) : "Never"}
              </Definition>
              <Definition term="Date of birth">{orDash(detail.date_of_birth)}</Definition>
              <Definition term="Gender">{detail.gender ? humanizeStatus(detail.gender) : "-"}</Definition>
              {/* Spec 16's registration extras. Read-only on purpose: captured
                  at registration for the UNO team. They are not in
                  EditMemberDialog. */}
              <Definition term="Referred by">{orDash(detail.referred_by)}</Definition>
              <Definition term="Country">{orDash(detail.country)}</Definition>
              <Definition term="City">{orDash(detail.city)}</Definition>
              <Definition term="Area code">{orDash(detail.area_id)}</Definition>
            </DefinitionList>
          </div>
        </div>
      </Card>

      {/* Every control here calls updateMember, which a deleted account refuses. */}
      {isDeleted ? null : (
        <UnoHandoffSection
          member={detail}
          onChanged={(message) => {
            setSaved(message);
            member.reload();
          }}
        />
      )}

      {/* Spec 22. Deletion removed the policy, and the API refuses a new one. */}
      {isDeleted ? null : <HealthPolicySection memberId={memberId} />}

      <Tabs
        value={tab}
        onChange={setTab}
        options={[
          { value: "overview", label: "Membership" },
          { value: "diet", label: "Diet activity" },
          // Deletion removed the listings, and the lookup is by the (now
          // placeholder) mobile number.
          ...(isDeleted ? [] : [{ value: "listings" as const, label: "FamilyBiz" }]),
          { value: "history", label: "Status history" },
        ]}
      />

      {tab === "overview" ? <MembershipSection memberId={memberId} /> : null}
      {tab === "diet" ? <DietSection memberId={memberId} /> : null}
      {tab === "listings" && !isDeleted ? <ListingsSection ownerMobile={detail.mobile_number} /> : null}
      {tab === "history" ? <StatusHistorySection member={detail} /> : null}

      {/* FR-8.4's "contact activity", which cannot be built from the API as it
          stands. Stated rather than shown as an empty table, because an empty
          table would read as "this member has never contacted anyone". */}
      <Card>
        <CardHeader
          title="Contact activity"
          hint="The contacts this member has initiated with partners and FamilyBiz listings."
        />
        <p className="notice" style={{ margin: 0 }}>
          Not available in this panel yet. The referral reports aggregate by partner or
          listing, not by member, and contact taps are member activity that is never written
          to the audit log - so there is no way to read one member's contact history with the
          API as it stands. To see contact volume, use{" "}
          <Link to="/referrals" style={{ textDecoration: "underline" }}>
            Referrals
          </Link>
          , which reports by target over a date range.
        </p>
      </Card>

      {editing ? (
        <EditMemberDialog
          member={detail}
          onClose={() => setEditing(false)}
          onDone={(message) => {
            setEditing(false);
            setSaved(message);
            member.reload();
          }}
        />
      ) : null}

      {changingStatus ? (
        <StatusDialog
          member={detail}
          onClose={() => setChangingStatus(false)}
          onDone={(message) => {
            setChangingStatus(false);
            setSaved(message);
            member.reload();
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteMemberDialog
          member={detail}
          onClose={() => setDeleting(false)}
          onDone={(message) => {
            setDeleting(false);
            setSaved(message);
            // The FamilyBiz tab is gone once the account is deleted.
            setTab("overview");
            member.reload();
          }}
        />
      ) : null}
    </Page>
  );
}

/**
 * Spec 16 - the UNO hand-off.
 *
 * The "UNO account approved" tick is a human relay: the front desk records here
 * what UNO decided. Un-ticking asks first - it is rarer than ticking and easy
 * to hit by accident.
 *
 * The valid-from / valid-to window is what the member's UNO card shows. The
 * server refuses the tick without both dates, so the tick stays disabled until
 * they are filled in, and ticking saves them in the same request.
 */
function UnoHandoffSection({
  member,
  onChanged,
}: {
  member: MemberDetail;
  onChanged: (message: string) => void;
}) {
  const [validFrom, setValidFrom] = useState(member.uno_valid_from ?? "");
  const [validTo, setValidTo] = useState(member.uno_valid_to ?? "");
  // ISO dates compare correctly as strings.
  const datesValid = validFrom !== "" && validTo !== "" && validTo >= validFrom;
  const datesChanged =
    validFrom !== (member.uno_valid_from ?? "") || validTo !== (member.uno_valid_to ?? "");

  const toggle = useMutation(
    (next: boolean) =>
      updateMember(
        member.id,
        next ? { uno_approved: true, uno_valid_from: validFrom, uno_valid_to: validTo } : { uno_approved: false },
      ),
    { onSuccess: (result) => onChanged(result?.message ?? "Member updated.") },
  );
  const saveDates = useMutation(
    () =>
      updateMember(member.id, {
        uno_valid_from: validFrom === "" ? null : validFrom,
        uno_valid_to: validTo === "" ? null : validTo,
      }),
    { onSuccess: (result) => onChanged(result?.message ?? "Member updated.") },
  );

  const unoApproved = member.uno_approved_at !== null;
  const busy = toggle.pending || saveDates.pending;

  let datesHint: string | null = null;
  if (validFrom !== "" && validTo !== "" && validTo < validFrom) {
    datesHint = "Valid to cannot be before valid from.";
  } else if (!unoApproved && !datesValid) {
    datesHint = "Enter both dates to turn on the UNO account.";
  }

  return (
    <Card>
      <CardHeader
        title="UNO hand-off"
        hint="Whether the UNO team has set up this member's account, and for how long."
      />

      <DefinitionList>
        <Definition term="UNO validity">
          <div className="stack" style={{ gap: 8 }}>
            <div className="form-grid">
              <Field label="Valid from">
                {(id) => (
                  <Input
                    id={id}
                    type="date"
                    value={validFrom}
                    disabled={busy}
                    onChange={(event) => setValidFrom(event.target.value)}
                  />
                )}
              </Field>
              <Field label="Valid to">
                {(id) => (
                  <Input
                    id={id}
                    type="date"
                    value={validTo}
                    min={validFrom || undefined}
                    disabled={busy}
                    onChange={(event) => setValidTo(event.target.value)}
                  />
                )}
              </Field>
            </div>
            {datesHint ? <span className="text-small text-meta">{datesHint}</span> : null}
            {!datesChanged && member.uno_valid_from && member.uno_valid_to ? (
              <span className="text-small text-meta">
                Card shows {formatDate(member.uno_valid_from)} to {formatDate(member.uno_valid_to)}
              </span>
            ) : null}
            {datesChanged && unoApproved ? (
              <div>
                <Button size="sm" disabled={busy || !datesValid} onClick={() => void saveDates.run()}>
                  {saveDates.pending ? "Saving…" : "Save dates"}
                </Button>
              </div>
            ) : null}
          </div>
        </Definition>
        <Definition term="UNO account">
          <label className="row row--tight" style={{ gap: 6, cursor: busy ? "default" : "pointer" }}>
            <input
              type="checkbox"
              checked={unoApproved}
              disabled={busy || (!unoApproved && !datesValid)}
              onChange={(event) => {
                const next = event.target.checked;
                if (!next && !confirmAction("Clear the UNO approval for this member?")) return;
                void toggle.run(next);
              }}
            />
            <span className="text-small">
              {unoApproved
                ? `Approved by UNO · marked ${formatDateTime(member.uno_approved_at as string)}`
                : "Not yet approved by UNO - tick once UNO confirms"}
            </span>
          </label>
        </Definition>
      </DefinitionList>

      <ErrorNotice error={toggle.error ?? saveDates.error} />
    </Card>
  );
}

/**
 * Spec 22 - the member's Health & Accident policy. The member app shows
 * exactly what is here: the file, plus the optional details.
 *
 * `file_url` is a signed link that expires (15 minutes by default). View checks
 * the expiry first and fetches a fresh link rather than opening a dead one.
 */
function HealthPolicySection({ memberId }: { memberId: string }) {
  const query = useQuery(() => fetchHealthPolicy(memberId), [memberId]);
  const policy = query.data?.policy ?? null;

  const [file, setFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState(() => detailsForm(null));

  // The form follows the saved policy whenever a new one arrives.
  useEffect(() => {
    setForm(detailsForm(query.data?.policy ?? null));
  }, [query.data]);

  const onWritten = (result: { message: string }) => {
    setNotice(result.message);
    query.reload();
  };
  const upload = useMutation((chosen: File) => uploadHealthPolicyFile(memberId, chosen), {
    onSuccess: (result) => {
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      onWritten(result);
    },
  });
  const save = useMutation(
    () =>
      updateHealthPolicy(memberId, {
        policy_number: form.policy_number.trim() || null,
        insurer: form.insurer.trim() || null,
        valid_until: form.valid_until || null,
      }),
    { onSuccess: onWritten },
  );
  const remove = useMutation(() => removeHealthPolicy(memberId), { onSuccess: onWritten });

  const busy = upload.pending || save.pending || remove.pending;
  const changed = JSON.stringify(form) !== JSON.stringify(detailsForm(policy));

  if (query.error) return <ErrorNotice error={query.error} onRetry={query.reload} />;
  if (query.loading && !query.data) {
    return (
      <Card flush>
        <LoadingRows rows={2} columns={3} />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Health & Accident policy"
        hint="The insurance policy this member sees under Health & Accident in the app."
        actions={
          policy ? (
            <>
              <a
                className="btn btn--ghost btn--sm"
                href={policy.file_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  // A link within a minute of expiring may die before the tab
                  // loads; fetch a fresh one instead of opening it.
                  if (Date.parse(policy.url_expires_at) - Date.now() < 60_000) {
                    event.preventDefault();
                    setNotice("The link had expired and has been refreshed. Click View again.");
                    query.reload();
                  }
                }}
              >
                View {policy.file_kind === "pdf" ? "PDF" : "image"}
              </a>
              <Button
                size="sm"
                variant="danger"
                disabled={busy}
                onClick={() => {
                  if (confirmAction("Remove this member's policy? They will no longer see it in the app.")) {
                    void remove.run();
                  }
                }}
              >
                {remove.pending ? "Removing…" : "Remove"}
              </Button>
            </>
          ) : null
        }
      />

      {notice ? <SuccessNotice message={notice} onDismiss={() => setNotice(null)} /> : null}

      <DefinitionList>
        <Definition term="Policy file">
          <div className="stack" style={{ gap: 8 }}>
            <span className="text-small">
              {policy
                ? `${policy.file_kind === "pdf" ? "PDF" : "Image"} · updated ${formatDateTime(policy.updated_at)}`
                : "Not uploaded yet. The member sees a 'not added yet' message."}
            </span>
            <div className="row row--tight">
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                disabled={busy}
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              <Button
                size="sm"
                disabled={!file || busy}
                onClick={() => {
                  if (file) void upload.run(file);
                }}
              >
                {upload.pending ? "Uploading…" : policy ? "Replace file" : "Upload file"}
              </Button>
            </div>
            <span className="text-small text-meta">
              PDF, JPEG, PNG or WebP up to 10 MB. Stored privately; links expire after 15 minutes.
            </span>
          </div>
        </Definition>
        <Definition term="Details">
          <div className="stack" style={{ gap: 8 }}>
            <div className="form-grid">
              <Field label="Policy number">
                {(id) => (
                  <Input
                    id={id}
                    value={form.policy_number}
                    maxLength={100}
                    disabled={!policy || busy}
                    onChange={(event) => setForm({ ...form, policy_number: event.target.value })}
                  />
                )}
              </Field>
              <Field label="Insurer">
                {(id) => (
                  <Input
                    id={id}
                    value={form.insurer}
                    maxLength={200}
                    disabled={!policy || busy}
                    onChange={(event) => setForm({ ...form, insurer: event.target.value })}
                  />
                )}
              </Field>
              <Field label="Valid until">
                {(id) => (
                  <Input
                    id={id}
                    type="date"
                    value={form.valid_until}
                    disabled={!policy || busy}
                    onChange={(event) => setForm({ ...form, valid_until: event.target.value })}
                  />
                )}
              </Field>
            </div>
            {policy ? (
              <div>
                <Button size="sm" disabled={!changed || busy} onClick={() => void save.run()}>
                  {save.pending ? "Saving…" : "Save details"}
                </Button>
              </div>
            ) : (
              <span className="text-small text-meta">Upload the policy file first, then add its details.</span>
            )}
          </div>
        </Definition>
      </DefinitionList>

      <ErrorNotice error={upload.error ?? save.error ?? remove.error} />
    </Card>
  );
}

function detailsForm(policy: HealthPolicy | null) {
  return {
    policy_number: policy?.policy_number ?? "",
    insurer: policy?.insurer ?? "",
    valid_until: policy?.valid_until ?? "",
  };
}

function MembershipSection({ memberId }: { memberId: string }) {
  const query = useQuery<MembershipList>(() => fetchMemberships(memberId), [memberId]);

  if (query.error) return <ErrorNotice error={query.error} onRetry={query.reload} />;
  if (query.loading && !query.data) {
    return (
      <Card flush>
        <LoadingRows rows={3} columns={4} />
      </Card>
    );
  }
  if (!query.data) return null;

  const { items, access } = query.data;

  return (
    <Card flush>
      <div style={{ padding: "24px 24px 16px" }}>
        <CardHeader
          title="Membership and access"
          hint="Whether the member can use the app right now, and the records behind that."
          actions={
            <Link to={`/memberships?member_id=${memberId}`} className="btn btn--ghost btn--sm">
              Manage memberships
            </Link>
          }
        />
        <DefinitionList>
          <Definition term="App access">
            {access.has_app_access ? (
              <Badge tone="live">
                <span className="dot" />
                Active
              </Badge>
            ) : (
              <Badge tone="danger">No access</Badge>
            )}
          </Definition>
          <Definition term="Days remaining">
            {access.days_remaining !== null && access.days_remaining !== undefined
              ? Math.max(0, access.days_remaining)
              : "-"}
          </Definition>
          <Definition term="In grace period">{access.in_grace_period ? "Yes" : "No"}</Definition>
          <Definition term="Access until">{formatDate(access.access_until)}</Definition>
        </DefinitionList>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="No membership records."
          body="Without one, the member cannot use the app."
          action={
            <Link to={`/memberships?member_id=${memberId}`} className="btn btn--accent btn--sm">
              Add a membership
            </Link>
          }
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Plan</th>
                <th scope="col">Period</th>
                <th scope="col" style={{ textAlign: "right" }}>
                  Paid
                </th>
                <th scope="col">Payment</th>
              </tr>
            </thead>
            <tbody>
              {items.map((membership) => (
                <tr key={membership.id}>
                  <td>
                    <div className="table__strong">{membership.plan_name}</div>
                    <div className="table__sub">
                      {membership.plan_features.slice(0, 2).join(" · ") || "No features listed"}
                    </div>
                  </td>
                  <td>
                    {formatDate(membership.start_date)} → {formatDate(membership.expiry_date)}
                    {membership.extension ? (
                      <div className="table__sub">extended from {formatDate(membership.original_expiry_date)}</div>
                    ) : null}
                  </td>
                  <td className="table__num">
                    {membership.amount_paid ? `${membership.currency} ${membership.amount_paid}` : "-"}
                  </td>
                  <td>
                    <div>{membership.payment_method ? humanizeStatus(membership.payment_method) : "Not recorded"}</div>
                    <div className="table__sub">
                      {membership.payment_date ? formatDate(membership.payment_date) : "-"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** FR-8.4's "diet plan activity": the generation ledger for this member. */
function DietSection({ memberId }: { memberId: string }) {
  const query = useQuery(
    () => fetchAiRequests({ user_id: memberId, limit: 25, offset: 0 }),
    [memberId],
  );

  if (query.error) return <ErrorNotice error={query.error} onRetry={query.reload} />;
  if (query.loading && !query.data) {
    return (
      <Card flush>
        <LoadingRows rows={4} columns={5} />
      </Card>
    );
  }
  if (!query.data) return null;

  const requests: AiRequestEntry[] = query.data.items;
  const succeeded = requests.filter((row) => row.outcome === "success").length;
  const charged = requests.filter((row) => row.counts_against_quota).length;

  return (
    <Card flush>
      <div style={{ padding: "24px 24px 16px" }}>
        <CardHeader
          title="Diet plan activity"
          hint="Generation attempts, newest first. A refused set of notes is free and is not charged to the member's daily allowance."
          actions={
            <Link to={`/ai-log?user_id=${memberId}`} className="btn btn--ghost btn--sm">
              Full log
            </Link>
          }
        />
        {requests.length > 0 ? (
          <div className="row" style={{ gap: 40 }}>
            <div>
              <span className="label">Attempts shown</span>
              <p className="figure" style={{ fontSize: 22, margin: "4px 0 0" }}>
                {formatCount(requests.length)}
              </p>
            </div>
            <div>
              <span className="label">Plans generated</span>
              <p className="figure" style={{ fontSize: 22, margin: "4px 0 0" }}>
                {formatCount(succeeded)}
              </p>
            </div>
            <div>
              <span className="label">Charged to quota</span>
              <p className="figure" style={{ fontSize: 22, margin: "4px 0 0" }}>
                {formatCount(charged)}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {requests.length === 0 ? (
        <EmptyState
          title="No diet plan activity."
          body="This member has not generated a plan yet."
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Request</th>
                <th scope="col">Classifier</th>
                <th scope="col">Outcome</th>
                <th scope="col">Charged</th>
                <th scope="col" style={{ textAlign: "right" }}>
                  Latency
                </th>
              </tr>
            </thead>
            <tbody>
              {requests.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.created_at)}</td>
                  <td>{row.request_type === "week" ? "Whole week" : `Day ${row.target_day_index ?? "?"}`}</td>
                  <td>
                    <Badge tone={row.classifier_verdict === "accepted" ? "active" : "muted"}>
                      {row.classifier_verdict}
                    </Badge>
                  </td>
                  <td>
                    <div>{row.outcome.replace(/_/g, " ")}</div>
                    {row.error_message ? <div className="table__sub">{row.error_message}</div> : null}
                  </td>
                  <td>{row.counts_against_quota ? `${row.quota_date}` : <span className="text-meta">Free</span>}</td>
                  <td className="table__num">
                    {row.latency_ms === null ? "-" : `${formatCount(row.latency_ms)} ms`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * This member's FamilyBiz listings.
 *
 * Found by the owner's mobile number, because `GET /admin/family-biz` filters by
 * `owner_q` (a name or mobile substring) rather than by owner id. The number is
 * unique in `users` (`users_mobile_uk`), so it identifies exactly one member.
 */
function ListingsSection({ ownerMobile }: { ownerMobile: string }) {
  const query = useQuery(() => fetchListings({ owner_q: ownerMobile, limit: 50, offset: 0 }), [ownerMobile]);

  if (query.error) return <ErrorNotice error={query.error} onRetry={query.reload} />;
  if (query.loading && !query.data) {
    return (
      <Card flush>
        <LoadingRows rows={3} columns={4} />
      </Card>
    );
  }
  if (!query.data) return null;

  const listings: ListingAdmin[] = query.data.items;

  return (
    <Card flush>
      <div style={{ padding: "24px 24px 16px" }}>
        <CardHeader
          title="FamilyBiz listings"
          hint="Matched on this member's mobile number, which is unique on their account."
          actions={
            listings.length > 0 ? (
              <Link to={`/family-biz?owner_q=${encodeURIComponent(ownerMobile)}`} className="btn btn--ghost btn--sm">
                Review in FamilyBiz
              </Link>
            ) : undefined
          }
        />
      </div>

      {listings.length === 0 ? (
        <EmptyState
          title="No listings."
          body="This member has not submitted a FamilyBiz listing."
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Listing</th>
                <th scope="col">Status</th>
                <th scope="col">Submitted</th>
                <th scope="col">Last note</th>
                <th scope="col" style={{ textAlign: "right" }}>
                  Open
                </th>
              </tr>
            </thead>
            <tbody>
              {listings.map((listing) => (
                <tr key={listing.id}>
                  <td className="table__strong">
                    {(listing.published ?? listing.pending ?? listing.draft)?.business_name ?? "Unnamed"}
                  </td>
                  <td>
                    <Badge tone={statusTone(listing.status)}>{humanizeStatus(listing.status)}</Badge>
                    {listing.requested_action ? (
                      <>
                        {" "}
                        <Badge tone="pending">asks to {listing.requested_action}</Badge>
                      </>
                    ) : null}
                  </td>
                  <td>{listing.submitted_at ? formatDate(listing.submitted_at) : "Never"}</td>
                  <td className="table__sub">{listing.latest_remarks ?? "-"}</td>
                  <td style={{ textAlign: "right" }}>
                    <Link to={`/family-biz/${listing.id}`} className="btn btn--ghost btn--sm">
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * The lifecycle events, from `user_status_events`.
 *
 * `to_status` can be `info_requested`, which is NOT a value `users.status` can
 * hold - it is an event-only state ("we asked them for something, and they are
 * still pending"). Rendering the events rather than the current status is what
 * makes that visible.
 */
function StatusHistorySection({ member }: { member: MemberDetail }) {
  return (
    <Card flush>
      <div style={{ padding: "24px 24px 16px" }}>
        <CardHeader
          title="Status history"
          hint="Every lifecycle change, with the note recorded at the time."
        />
      </div>
      {member.status_events.length === 0 ? (
        <EmptyState title="No status changes recorded." body="This member's status has not changed since registration." />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">From</th>
                <th scope="col">To</th>
                <th scope="col">Note</th>
              </tr>
            </thead>
            <tbody>
              {member.status_events.map((event) => (
                <tr key={event.id}>
                  <td>{formatDateTime(event.created_at)}</td>
                  <td>{event.from_status ?? "-"}</td>
                  <td>
                    <Badge tone={statusTone(event.to_status ?? "")}>
                      {humanizeStatus(event.to_status)}
                    </Badge>
                  </td>
                  <td>{event.remarks ?? <span className="text-meta">-</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * FR-3.3.3's profile edit.
 *
 * Only the fields `account_service.ADMIN_EDITABLE_FIELDS` allows are offered, and
 * `role` is not among them - FR-8.1b's "no self-service role elevation" means the
 * server refuses it (`422`), so a control here would always error.
 */
function EditMemberDialog({
  member,
  onClose,
  onDone,
}: {
  member: MemberDetail;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [fullName, setFullName] = useState(member.full_name);
  const [mobile, setMobile] = useState(member.mobile_number);
  const [email, setEmail] = useState(member.email ?? "");
  const [isDemo, setIsDemo] = useState(member.is_demo_account);

  const mutation = useMutation(
    () => {
      const changes: MemberUpdate = {
        full_name: fullName.trim(),
        mobile_number: mobile.trim(),
        email: email.trim() === "" ? null : email.trim(),
        is_demo_account: isDemo,
      };
      return updateMember(member.id, changes);
    },
    { onSuccess: (result) => onDone(result?.message ?? "Member updated.") },
  );

  return (
    <Modal
      title="Edit profile"
      description="Changes are audited. Changing a mobile number changes what the member signs in with."
      onClose={onClose}
      wide
    >
      <div className="stack" style={{ gap: 16 }}>
        <div className="form-grid">
          <Field label="Full name (required)">
            {(id) => (
              <Input id={id} value={fullName} onChange={(event) => setFullName(event.target.value)} maxLength={160} />
            )}
          </Field>
          <Field label="Mobile number (required)" hint="Unique across all accounts.">
            {(id) => (
              <Input id={id} value={mobile} onChange={(event) => setMobile(event.target.value)} maxLength={20} />
            )}
          </Field>
          <Field label="Email">
            {(id) => (
              <Input
                id={id}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                maxLength={254}
              />
            )}
          </Field>
          <label className="row row--tight" style={{ gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={isDemo} onChange={(event) => setIsDemo(event.target.checked)} />
            <span className="text-small">Demo account</span>
          </label>
        </div>

        <ErrorNotice error={mutation.error} />

        <div className="modal__actions">
          <Button onClick={onClose} disabled={mutation.pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!fullName.trim() || !mobile.trim() || mutation.pending}
            onClick={() => void mutation.run()}
          >
            {mutation.pending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * A status change from the member's own page (FR-3.3.3 / FR-3.1.5).
 *
 * This is the path for a status that is NOT the registration decision - suspending
 * a member, reactivating one, or setting one back to pending. Each goes through
 * `account_service.change_status`, so it writes the event, the audit entry and the
 * member's notification.
 */
function StatusDialog({
  member,
  onClose,
  onDone,
}: {
  member: MemberDetail;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [status, setStatus] = useState<MemberStatus>(member.status);
  const [remarks, setRemarks] = useState("");

  const mutation = useMutation(() => updateMember(member.id, { status, remarks: remarks.trim() || null }), {
    onSuccess: (result) => onDone(result?.message ?? "Status updated."),
  });

  const dangerous = status === "suspended" || status === "rejected";

  return (
    <Modal
      title="Change this member's status"
      description={
        <>
          Currently <strong>{member.status}</strong>. The member is notified, and the change is
          recorded against your account.
        </>
      }
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 16 }}>
        <Field label="New status">
          {(id) => (
            <Select
              id={id}
              value={status}
              onChange={(event) => setStatus(event.target.value as MemberStatus)}
            >
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Note for the member"
          hint="Shown to them with the decision, and kept on the account's history."
        >
          {(id) => (
            <Textarea id={id} value={remarks} onChange={(event) => setRemarks(event.target.value)} maxLength={1000} />
          )}
        </Field>

        <ErrorNotice error={mutation.error} />

        <div className="modal__actions">
          <Button onClick={onClose} disabled={mutation.pending}>
            Cancel
          </Button>
          <Button
            variant={dangerous ? "danger" : "accent"}
            disabled={status === member.status || mutation.pending}
            onClick={() => void mutation.run()}
          >
            {mutation.pending ? "Updating…" : `Set to ${status}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Spec 19 - delete a member's account on their behalf, for a request made by
 * email or at the front desk. The same erasure as the member's own Delete
 * account, and irreversible, so the admin types the member's name to confirm
 * (checked here only: it guards against the wrong click, not the wrong admin).
 * The member is not notified.
 */
function DeleteMemberDialog({
  member,
  onClose,
  onDone,
}: {
  member: MemberDetail;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [typedName, setTypedName] = useState("");
  const [remarks, setRemarks] = useState("");

  const mutation = useMutation(() => deleteMember(member.id, remarks.trim() || null), {
    onSuccess: (result) => onDone(result?.message ?? "Account deleted."),
  });

  const normalise = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();
  const confirmed = normalise(typedName) === normalise(member.full_name);

  return (
    <Modal
      title="Delete this member's account"
      description={
        <>
          This removes their profile, photo, weight entries, diet plans and FamilyBiz listings,
          and signs them out everywhere. Membership and payment records, status history and the
          audit log are kept. <strong>This can't be undone</strong>, and the member is not
          notified.
        </>
      }
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 16 }}>
        <Field
          label={
            <>
              Type <strong>{member.full_name}</strong> to confirm
            </>
          }
        >
          {(id) => (
            <Input
              id={id}
              value={typedName}
              onChange={(event) => setTypedName(event.target.value)}
              autoComplete="off"
              maxLength={160}
            />
          )}
        </Field>

        <Field label="Reason (optional)" hint="Kept on the member's status history.">
          {(id) => (
            <Textarea id={id} value={remarks} onChange={(event) => setRemarks(event.target.value)} maxLength={1000} />
          )}
        </Field>

        <ErrorNotice error={mutation.error} />

        <div className="modal__actions">
          <Button onClick={onClose} disabled={mutation.pending}>
            Cancel
          </Button>
          <Button variant="danger" disabled={!confirmed || mutation.pending} onClick={() => void mutation.run()}>
            {mutation.pending ? "Deleting…" : "Delete account"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
